import React, { useState, useEffect, useCallback } from "react";
import {
  addAIPromptMaster,
  updateAIPromptMaster,
  getAIPromptMasterById,
} from "../../services/aiPromptMastersService";
import { fetchPageMasters } from "../../services/pageMasterService";
import { validateAIPromptMaster } from "../../utils/validation";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { handleErrors } from "../../utils/errorHandler";
import ComponentHeader from "../Common/OtherElements/ComponentHeader";
import PromptContentEditor from "./PromptContentEditor";

const INITIAL_FORM_STATE = {
  pageId: "",
  promptName: "",
  promptDescription: "",
  promptContent: "",
  isDefault: false,
};

const INITIAL_ERRORS_STATE = {
  pageId: "",
  promptName: "",
  promptDescription: "",
  promptContent: "",
};

const mapPromptResponse = (data) => ({
  pageId:
    data.pageId !== undefined && data.pageId !== null
      ? String(data.pageId)
      : data.PageId !== undefined && data.PageId !== null
      ? String(data.PageId)
      : "",
  promptName: data.promptName || data.PromptName || "",
  promptDescription: data.promptDescription || data.PromptDescription || "",
  promptContent: data.promptContent || data.PromptContent || "",
  isDefault: Boolean(data.isDefault ?? data.IsDefault ?? false),
});

export const AddAIPrompt = ({
  editMode = false,
  initialData = {},
  onSuccess,
  setSelectedPrompt,
  setEditMode,
}) => {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState(INITIAL_ERRORS_STATE);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [pageOptions, setPageOptions] = useState([]);

  useEffect(() => {
    const fetchPages = async () => {
      try {
        const list = await fetchPageMasters();
        setPageOptions(Array.isArray(list) ? list : []);
      } catch (error) {
        handleErrors(error);
      }
    };
    fetchPages();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const promptId = initialData?.id ?? initialData?.Id;
      if (editMode && promptId) {
        try {
          const data = await getAIPromptMasterById(promptId);
          setFormData(mapPromptResponse(data));
        } catch (error) {
          handleErrors(error);
        }
      } else {
        setFormData(INITIAL_FORM_STATE);
      }
    };
    fetchData();
  }, [editMode, initialData]);

  const handleInputChange = ({ target: { name, value } }) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handlePromptContentChange = (value) => {
    setFormData((prev) => ({ ...prev, promptContent: value }));
    setErrors((prev) => ({ ...prev, promptContent: "" }));
  };

  const handleIsDefaultChange = ({ target: { checked } }) => {
    setFormData((prev) => ({ ...prev, isDefault: checked }));
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM_STATE);
    setErrors(INITIAL_ERRORS_STATE);
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const { valid, errors: validationErrors } = validateAIPromptMaster(formData);
      setErrors(validationErrors);

      if (!valid) return;

      const promptId = initialData?.id ?? initialData?.Id;
      const payload = {
        pageId: Number(formData.pageId),
        promptName: formData.promptName.trim(),
        promptDescription: formData.promptDescription?.trim() || "",
        promptContent: formData.promptContent,
        isDefault: Boolean(formData.isDefault),
      };

      if (editMode) {
        payload.id = promptId;
      }

      try {
        setIsButtonDisabled(true);
        const action = editMode ? updateAIPromptMaster : addAIPromptMaster;
        const response = await action(payload);

        if (response?.data?.isSuccess === false) {
          toast.error(
            response.data.message || "Request failed. Please try again."
          );
          return;
        }

        toast.success(
          `AI prompt ${editMode ? "updated" : "added"} successfully!`
        );
        resetForm();
        if (onSuccess) onSuccess();
        setEditMode(false);
      } catch (error) {
        handleErrors(error);
      } finally {
        setIsButtonDisabled(false);
      }
    },
    [formData, editMode, initialData, onSuccess, setEditMode]
  );

  const handleAddNewClick = () => {
    resetForm();
    setSelectedPrompt(null);
    setEditMode(false);
  };

  const getPageLabel = (page) =>
    page.pageName || page.PageName || page.pageLink || `Page ${page.id ?? page.Id}`;

  return (
    <>
      <ComponentHeader title="AI Prompt Masters" />

      <div className="row">
        <div className="col-xxl-12">
          <div className="card mt-xxl-n5">
            <div className="card-header">
              <h5 className="mb-sm-1 mt-sm-1">
                {editMode ? "Update AI Prompt" : "Add AI Prompt"}
              </h5>
            </div>

            <div className="card-body p-4">
              <form onSubmit={handleSubmit} method="POST">
                <div className="row">
                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <div className="mb-3">
                      <label htmlFor="pageId" className="form-label">
                        Page <span className="required-field">*</span>
                      </label>
                      <select
                        name="pageId"
                        id="pageId"
                        value={formData.pageId}
                        onChange={handleInputChange}
                        className={`form-select ${
                          errors.pageId ? "is-invalid" : ""
                        }`}
                      >
                        <option value="">Select Page</option>
                        {pageOptions.map((page) => {
                          const id = page.id ?? page.Id;
                          return (
                            <option key={id} value={String(id)}>
                              {getPageLabel(page)}
                            </option>
                          );
                        })}
                      </select>
                      {errors.pageId && (
                        <div className="invalid-feedback">{errors.pageId}</div>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <div className="mb-3">
                      <label htmlFor="promptName" className="form-label">
                        Prompt Name <span className="required-field">*</span>
                      </label>
                      <input
                        type="text"
                        name="promptName"
                        id="promptName"
                        placeholder="Enter Prompt Name"
                        value={formData.promptName}
                        onChange={handleInputChange}
                        className={`form-control ${
                          errors.promptName ? "is-invalid" : ""
                        }`}
                      />
                      {errors.promptName && (
                        <div className="invalid-feedback">
                          {errors.promptName}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <div className="mb-3">
                      <label className="form-label d-block">Is Default</label>
                      <div className="form-check form-switch form-switch-custom form-switch-primary">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="promptIsDefault"
                          checked={formData.isDefault}
                          onChange={handleIsDefaultChange}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="promptIsDefault"
                        >
                          Set as default prompt for page
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-12">
                    <div className="mb-3">
                      <label htmlFor="promptDescription" className="form-label">
                        Prompt Description
                      </label>
                      <textarea
                        name="promptDescription"
                        id="promptDescription"
                        rows="3"
                        placeholder="Optional description for this prompt"
                        value={formData.promptDescription}
                        onChange={handleInputChange}
                        className={`form-control ${
                          errors.promptDescription ? "is-invalid" : ""
                        }`}
                      />
                      {errors.promptDescription && (
                        <div className="invalid-feedback">
                          {errors.promptDescription}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Prompt Content{" "}
                        <span className="required-field">*</span>
                      </label>
                      <PromptContentEditor
                        value={formData.promptContent}
                        onChange={handlePromptContentChange}
                        error={errors.promptContent}
                        disabled={isButtonDisabled}
                      />
                    </div>
                  </div>

                  <div className="col-lg-12">
                    <div>
                      <button
                        type="submit"
                        className="btn btn-secondary pt-1 pb-1 p-3"
                        disabled={isButtonDisabled}
                      >
                        {isButtonDisabled
                          ? editMode
                            ? "Updating"
                            : "Saving"
                          : editMode
                          ? "Update"
                          : "Save"}
                      </button>
                      {editMode && (
                        <button
                          type="button"
                          onClick={handleAddNewClick}
                          className="btn btn-danger ms-1 pt-1 pb-1 p-3"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
